import { useRef, useState } from "react";
import type { EvidenciaOut } from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";
import { fmtBytes } from "@/lib/formato";

const MAX_INLINE_BYTES = 1024 * 1024;

type InlineResult = { tipo: "inline"; dataUrl: string; alt: string };
type EvidResult = {
  tipo: "evidencia";
  evidencia: EvidenciaOut;
  bloqueHtml: string;
};
export type ImagenInsertada = InlineResult | EvidResult;

type Props = {
  open: boolean;
  onClose: () => void;
  casoId: number;
  /** Llamado tras procesar el archivo (inline o subido a evidencias). */
  onInsertar: (resultado: ImagenInsertada) => void;
};

/**
 * Modal de insercion de imagen al editor del Mensaje al cliente.
 *
 * Drag-and-drop o click para seleccionar. La logica decide automatico:
 * - ≤1MB: data URL inline (cliente lo ve embedded en el correo).
 * - >1MB: sube como Evidencia del caso con tag "mensaje-cliente-img" e
 *   inserta un bloque referencial en el editor. Cuando el correo se mande
 *   con ZIP de evidencias, esta imagen va incluida.
 */
export default function InsertarImagenModal({
  open,
  onClose,
  casoId,
  onInsertar,
}: Props) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [working, setWorking] = useState<"" | "leyendo" | "subiendo">("");

  function reset() {
    setDragOver(false);
    setWorking("");
  }

  async function procesar(file: File) {
    if (!file.type.startsWith("image/")) {
      toast.error("Ese archivo no es imagen", file.type || "tipo desconocido");
      return;
    }

    if (file.size <= MAX_INLINE_BYTES) {
      setWorking("leyendo");
      try {
        const dataUrl: string = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.onerror = () => reject(r.error);
          r.readAsDataURL(file);
        });
        onInsertar({ tipo: "inline", dataUrl, alt: file.name });
        reset();
        onClose();
      } catch {
        toast.error("No pude leer el archivo");
        setWorking("");
      }
      return;
    }

    // >1MB → sube a evidencias del caso
    setWorking("subiendo");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tags", "mensaje-cliente-img");
      const res = await fetch(`/api/casos/${casoId}/evidencias`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        let detail = res.statusText;
        try {
          const data = await res.json();
          detail = data.detail ?? detail;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const evid = (await res.json()) as EvidenciaOut;
      const tam = fmtBytes(file.size);
      // Bloque de referencia que se inserta en el editor. Al sanitizar/enviar
      // el correo, los inline styles se conservan.
      const safeName = escapeHtml(file.name);
      const bloqueHtml = `<blockquote>📎 <strong>${safeName}</strong> · ${tam} · adjunto en evidencias del caso (se incluye en el ZIP del correo).</blockquote><p></p>`;
      onInsertar({ tipo: "evidencia", evidencia: evid, bloqueHtml });
      toast.success("Imagen subida a evidencias", `${file.name} · ${tam}`);
      reset();
      onClose();
    } catch (err) {
      toast.error("No se pudo subir", err instanceof Error ? err.message : "Error");
      setWorking("");
    }
  }

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void procesar(f);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (working) return;
    const f = e.dataTransfer.files?.[0];
    if (f) void procesar(f);
  }

  function handleClose() {
    if (working) return; // no cerrar mientras hay upload en curso
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && handleClose()}
      title="Insertar imagen"
      description="Arrastra una imagen aquí o haz click para seleccionar. ≤1MB se incrusta dentro del mensaje; más grandes se suben a Evidencias y se incluyen en el ZIP del correo."
      size="md"
      footer={
        <button onClick={handleClose} className="btn-ghost" disabled={!!working}>
          {working ? "Espera..." : "Cancelar"}
        </button>
      }
    >
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!working) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !working && fileInputRef.current?.click()}
        className={`rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition select-none ${
          working
            ? "border-ink-700 bg-ink-800/40 cursor-wait"
            : dragOver
            ? "border-accent-500 bg-accent-500/5"
            : "border-ink-700 hover:border-accent-500/60 hover:bg-ink-800/40"
        }`}
      >
        {working === "subiendo" ? (
          <>
            <div className="text-2xl mb-2">⏳</div>
            <p className="text-sm text-zinc-300 font-medium">
              Subiendo a evidencias...
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              El archivo se incluirá en el ZIP cuando mandes el correo.
            </p>
          </>
        ) : working === "leyendo" ? (
          <>
            <div className="text-2xl mb-2">⏳</div>
            <p className="text-sm text-zinc-300 font-medium">Procesando...</p>
          </>
        ) : (
          <>
            <div className="text-3xl mb-2">🖼</div>
            <p className="text-sm text-zinc-200 font-medium">
              Arrastra la imagen aquí
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              o haz click para seleccionar
            </p>
            <p className="text-xs text-zinc-500 mt-3">
              <span className="text-emerald-400/80">≤1MB</span> se inserta dentro del mensaje ·{" "}
              <span className="text-amber-400/80">&gt;1MB</span> va a Evidencias
              automático
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFileChosen}
        />
      </div>
    </Modal>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
